import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Loader from '../Components/Loader';
import SuccessCard from '../Components/SuccessCard';
import ErrorCard from '../Components/ErrorCard';
import useUpdateNIS from '../Hooks/NisFsResultHooks/useUpdateNisFsResult';
import useIAList from '../Hooks/NisHooks/useIAList';
import useISList from '../Hooks/NisHooks/useISList';
import MunicipalitySelector from '../Components/Municipality';
import MailingAddressSelector from '../Components/MailingAddressSelector';
import adminBg from '../Assets/bg/adminbg.jpg';


// Error Boundary Component
class ErrorBoundary extends React.Component {
state = { hasError: false, error: null };

static getDerivedStateFromError(error) {
  return { hasError: true, error };
}

render() {
  if (this.state.hasError) {
    return (
      <ErrorCard
        errorMessage={`Rendering error: ${this.state.error?.message || 'Unknown error'}`}
        onClose={this.props.onClose}
      />
    );
  }
  return this.props.children;
}
}


const NISFsResultEdit = ({ selectedItem, onClose, darkMode }) => {
  const { updateNIS, isLoading, error, success } = useUpdateNIS();
  const { iaList, loading: iaListLoading, error: iaListError } = useIAList();
  const { isList, loading: isListLoading, error: isListError } = useISList();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});


  
  // Initial form data
  const initialFormData = {
    is_name: '',
    years: '',
    ia_name: '',
    om_1: '',
    om_2: '',
    om_3: '',
    om_r: '',
    financial_1: '',
    financial_2: '',
    financial_3: '',
    financial_4: '',
    financial_5: '',
    financial_6: '',
    financial_7: '',
    financial_8: '',
    financial_r: '',
    OOD_1: '',
    OOD_2: '',
    OOD_3: '',
    OOD_4: '',
    OOD_5: '',
    OOD_6: '',
    OOD_7: '',
    OOD_8: '',
    OOD_9: '',
    OOD_10: '',
    OOD_11: '',
    OOD_12: '',
    OOD_13: '',
    OOD_14: '',
    OOD_r: '',
    linkages_1: '',
    linkages_2: '',
    linkages_3: '',
    linkages_4: '',
    linkages_5: '',
    linkages_r: '',
    specialFeatures_1: '',
    specialFeatures_2: '',
    specialFeatures_3: '',
    specialFeatures_4: '',
    specialFeatures_5: '',
    specialFeatures_r: '',
    total: '',
    adjective: '',
    custom_is_name: '',
    custom_ia_name: '',
  };

  // Convert date to YYYY-MM-DD format
  const convertDateFormat = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  // Initialize form state
  const [formData, setFormData] = useState({
    is_name: selectedItem?.is_name || '',
    years: selectedItem?.years?.toString() || '',
    ia_name: selectedItem?.ia_name || '',
    om_1: selectedItem?.om_1 || '',
    om_2: selectedItem?.om_2 || '',
    om_3: selectedItem?.om_3 || selectedItem?.mailingAddress || '', // Fallback to mailingAddress
    om_r: selectedItem?.om_r || '',
    financial_1: selectedItem?.financial_1 || '',
    financial_2: convertDateFormat(selectedItem?.financial_2) || '',
    financial_2: selectedItem?.financial_2 || '',
    financial_3: convertDateFormat(selectedItem?.financial_3) || '',
    financial_4: selectedItem?.financial_4 || '',
    financial_5: selectedItem?.financial_5 || '',
    financial_6: selectedItem?.financial_6 || '',
    financial_7: selectedItem?.financial_7 || '',
    financial_8: selectedItem?.financial_8 || '',
    financial_r: selectedItem?.financial_r || '',
    OOD_1: selectedItem?.OOD_1 || '',
    OOD_2: selectedItem?.OOD_2 || '',
    OOD_3: selectedItem?.OOD_3 || '',
    OOD_4: selectedItem?.OOD_5 || '',
    OOD_5: selectedItem?.OOD_6 || '',
    OOD_6: selectedItem?.OOD_7 || '',
    OOD_7: selectedItem?.OOD_8 || '',
    OOD_8: selectedItem?.OOD_9 || '',
    OOD_9: selectedItem?.OOD_10 || '',
    OOD_10: selectedItem?.OOD_11 || '',
    OOD_11: selectedItem?.OOD_12 || '',
    OOD_12: selectedItem?.OOD_13 || '',
    OOD_13: selectedItem?.OOD_14 || '',
    OOD_14: selectedItem?.OOD_15 || '',
    OOD_r: selectedItem?.OOD_r || '',
    linkages_1: selectedItem?.linkages_1 || '',
    linkages_2: selectedItem?.linkages_2 || '',
    linkages_3: selectedItem?.linkages_3 || '',
    linkages_4: selectedItem?.linkages_4 || '',
    linkages_5: selectedItem?.linkages_5 || '',
    linkages_r: selectedItem?.linkages_r || '',
    specialFeatures_1: selectedItem?.specialFeatures_1 || '',
    specialFeatures_2: selectedItem?.specialFeatures_2 || '',
    specialFeatures_3: selectedItem?.specialFeatures_3 || '',
    specialFeatures_4: selectedItem?.specialFeatures_4 || '',
    specialFeatures_5: selectedItem?.specialFeatures_5 || '',
    specialFeatures_r: selectedItem?.specialFeatures_r || '',
    total: selectedItem?.total || '',
    adjective: selectedItem?.adjective || '',
    custom_is_name: '',
    custom_ia_name: '',
  });

  const formSections = [
    { fields: ['years'] },
    { fields: ['ia_name', 'is_name'] },
    { title: 'O & M*', fields: ['om_1', 'om_2', 'om_3', 'om_r'] },
    { title: 'FINANCIAL', fields: ['financial_1', 'financial_2', 'financial_3', 'financial_4', 'financial_5', 'financial_6', 'financial_7', 'financial_8', 'financial_r'] },
    { title: 'ORGANIZATION AND ORGANIZATIONAL DISCIPLINE', fields: ['OOD_1', 'OOD_2', 'OOD_3', 'OOD_4', 'OOD_5', 'OOD_6', 'OOD_7', 'OOD_8', 'OOD_9', 'OOD_10', 'OOD_11', 'OOD_12', 'OOD_13', 'OOD_14', 'OOD_r'] },
    { title: 'Assistance Program/Linkages', fields: ['linkages_1', 'linkages_2', 'linkages_3', 'linkages_4', 'linkages_5', 'linkages_r'] },
    { title: 'Special Features', fields: ['specialFeatures_1', 'specialFeatures_2', 'specialFeatures_3', 'specialFeatures_4', 'specialFeatures_5', 'specialFeatures_r'] },
    { title: 'Ave. IA Ratings', fields: ['total', 'adjective'] },
  ];


  const fieldLabelMap = {
    is_name: 'IS Name',
    years: 'Year',
    ia_name: "Name of Irrigators' Association",
    om_1: '1',
    om_2: '2',
    om_3: '3',
    om_r: 'R',
    financial_1: '1',
    financial_2: '2',
    financial_3: '3',
    financial_4: '4',
    financial_5: '5',
    financial_6: '6',
    financial_7: '7',
    financial_8: '8',
    financial_r: 'R',
    OOD_1: '1',
    OOD_2: '2',
    OOD_3: '3',
    OOD_4: '4',
    OOD_5: '5',
    OOD_6: '6',
    OOD_7: '7',
    OOD_8: '8',
    OOD_9: '9',
    OOD_10: '10',
    OOD_11: '11',
    OOD_12: '12',
    OOD_13: '13',
    OOD_14: '14',
    OOD_r: 'R',
    linkages_1: '1',
    linkages_2: '2',
    linkages_3: '3',
    linkages_4: '4',
    linkages_5: '5',
    linkages_r: 'R',
    specialFeatures_1: '1',
    specialFeatures_2: '2',
    specialFeatures_3: '3',
    specialFeatures_4: '4',
    specialFeatures_5: '5',
    specialFeatures_r: 'R',
    total: 'Total',
    adjective: 'Adjective',
  };

  // Generate year options (2000 to current year)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 2000 + 1 }, (_, i) => (currentYear - i).toString());

  // Log for debugging
  useEffect(() => {
    console.log('NISEdit received selectedItem:', selectedItem);
    console.log('formData:', formData);
    console.log('Problematic fields:', {
      years: formData.years,
      municipality: formData.municipality,
      mailing_address: formData.mailing_address,
    });
    console.log('iaList:', iaList);
    console.log('isList:', isList);
    if (!selectedItem || !selectedItem.id) {
      console.warn('Invalid selectedItem');
      setShowError(true);
    }
  }, [selectedItem, iaList, isList, formData]);
  


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  const validateForm = () => {
    const errors = {};
    Object.keys(formData).forEach((field) => {
      if (
        (field === 'custom_is_name' && formData.is_name !== 'Others ...') ||
        (field === 'custom_ia_name' && formData.ia_name !== 'Others ...')
      ) {
        return;
      }
  
      const value = formData[field];
  
      if (
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '') ||
        (typeof value !== 'string' && value === '') // covers numeric zeroes and non-strings
      ) {
        errors[field] = true;
      }
    });
  
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setShowError(true);
      console.log('Validation errors:', validationErrors);
      return;
    }
    const submissionData = {
      ...formData,
      is_name: formData.is_name === 'Others ...' ? formData.custom_is_name.toUpperCase() : formData.is_name,
      ia_name: formData.ia_name === 'Others ...' ? formData.custom_ia_name.toUpperCase() : formData.ia_name,
    };
    try {
      await updateNIS(selectedItem.id, submissionData);
      setShowSuccess(true);
      setShowError(false);
      setTimeout(onClose, 1000);
    } catch (err) {
      setShowError(true);
      setShowSuccess(false);
      console.error('Update error:', err);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setValidationErrors({});
    setShowSuccess(false);
    setShowError(false);
    onClose();
  };
  

  useEffect(() => {
    console.log('selectedItem:', selectedItem);
    console.log('selectedItem.years:', selectedItem?.years);
    console.log('selectedItem.year:', selectedItem?.year);
  }, [selectedItem]);
  

  if (!selectedItem || !selectedItem.id) {
    return (
      <RightPanel darkMode={darkMode} show={true}>
        <FormContainer darkMode={darkMode}>
          <Title darkMode={darkMode}>
            <span>Edit NIS FS Result Record</span>
            <CloseButton onClick={handleClose} darkMode={darkMode}>
            X
          </CloseButton>
          </Title>
          <ErrorCard errorMessage="No valid record selected." onClose={handleClose} />
        </FormContainer>
      </RightPanel>
    );
  }

  

  return (
    <RightPanel darkMode={darkMode} show={true}>
      <FormContainer darkMode={darkMode}>
        <Title darkMode={darkMode}>
          <span>Edit NIS FS Result Record</span>
          <CloseButton onClick={handleClose} darkMode={darkMode}>
            X
          </CloseButton>
        </Title>
        {(isLoading || iaListLoading || isListLoading) && (
          <LoaderOverlay>
            <Loader />
          </LoaderOverlay>
        )}
        {(error || iaListError || isListError) && (
          <ErrorMessage>{error || iaListError || isListError}</ErrorMessage>
        )}
        {showSuccess && (
          <SuccessCard
            message="NIS data updated successfully!"
            onClose={() => setShowSuccess(false)}
          />
        )}
        {showError && (
          <ErrorMessage>Please fill out all fields correctly.</ErrorMessage>
        )}
        <ErrorBoundary onClose={handleClose}>
          <StyledForm onSubmit={handleSubmit}>
            {formSections.map((section, index) => (
              <CardSection darkMode={darkMode} key={index}>
                {section.title && (
                  <SectionHeader darkMode={darkMode}>{section.title}</SectionHeader>
                )}
                {section.fields.map((key) => (
                  <FieldGroup key={key} darkMode={darkMode} hasError={validationErrors[key]}>
                    <Label htmlFor={key} hasError={validationErrors[key]}>
                      {fieldLabelMap[key]}
                      <RequiredAsterisk>*</RequiredAsterisk>
                    </Label>
                    {key === 'years' ? (
  (() => {
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: currentYear - 2000 + 1 }, (_, i) => (currentYear - i).toString());

    const options = yearOptions.includes(formData.years)
      ? yearOptions
      : [formData.years, ...yearOptions];

    return (
      <Select
        id={key}
        name={key}
        value={formData[key]}
        onChange={handleChange}
        aria-label={fieldLabelMap[key]}
        darkMode={darkMode}
        hasError={validationErrors[key]}
      >
        <option value="">Select Year</option>
        {options.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </Select>
    );
  })()
) : key === 'is_name' ? (
                      <>
                        <Select
                          id={key}
                          name={key}
                          value={formData[key]}
                          onChange={handleChange}
                          aria-label={fieldLabelMap[key]}
                          darkMode={darkMode}
                          hasError={validationErrors[key]}
                        >
                          <option value="">Select IS Name</option>
                          {isList?.length > 0 ? (
                            isList.map((is) => (
                              <option key={is.is_name} value={is.is_name}>
                                {is.is_name}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>
                              No IS names available
                            </option>
                          )}
                          <option value="Others ...">Add new IS</option>
                        </Select>
                        {formData.is_name === 'Others ...' && (
                          <Input
                            type="text"
                            id="custom_is_name"
                            name="custom_is_name"
                            value={formData.custom_is_name}
                            onChange={handleChange}
                            placeholder="Enter Custom IS Name"
                            aria-label={fieldLabelMap.custom_is_name}
                            darkMode={darkMode}
                            hasError={validationErrors.custom_is_name}
                            style={{ marginTop: '8px' }}
                          />
                        )}
                      </>
                    ) : key === 'ia_name' ? (
                      <>
                        <Select
                          id={key}
                          name={key}
                          value={formData[key]}
                          onChange={handleChange}
                          aria-label={fieldLabelMap[key]}
                          darkMode={darkMode}
                          hasError={validationErrors[key]}
                        >
                          <option value="">Select IA Name</option>
                          {iaList?.length > 0 ? (
                            iaList.map((ia) => (
                              <option key={ia.ia_name} value={ia.ia_name}>
                                {ia.ia_name}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>
                              No IA names available
                            </option>
                          )}
                          <option value="Others ...">Add new IA</option>
                        </Select>
                        {formData.ia_name === 'Others ...' && (
                          <Input
                            type="text"
                            id="custom_ia_name"
                            name="custom_ia_name"
                            value={formData.custom_ia_name}
                            onChange={handleChange}
                            placeholder="Enter Custom IA Name"
                            aria-label={fieldLabelMap.custom_ia_name}
                            darkMode={darkMode}
                            hasError={validationErrors.custom_ia_name}
                            style={{ marginTop: '8px' }}
                          />
                        )}
                      </>
                    )   : key === 'om_1' ||
                      key === 'om_2' ||
                      key === 'om_3' ||
                      key === 'om_r' ||
                      key === 'financial_1' ||
                      key === 'financial_2' ||
                      key === 'financial_3' ||
                      key === 'financial_4' ||
                      key === 'financial_5' ||
                      key === 'financial_6' ||
                      key === 'financial_7' ||
                      key === 'financial_8' ||
                      key === 'financial_r' ||
                      key === 'OOD_1' ||
                      key === 'OOD_2' ||
                      key === 'OOD_3' ||
                      key === 'OOD_4' ||
                      key === 'OOD_5' ||
                      key === 'OOD_6' ||
                      key === 'OOD_7' ||
                      key === 'OOD_8' ||
                      key === 'OOD_9' ||
                      key === 'OOD_10' ||
                      key === 'OOD_11' ||
                      key === 'OOD_12' ||
                      key === 'OOD_13' ||
                      key === 'OOD_14' ||
                      key === 'OOD_r' ||
                      key === 'linkages_1' ||
                      key === 'linkages_2' ||
                      key === 'linkages_3' ||
                      key === 'linkages_4' ||
                      key === 'linkages_5' ||
                      key === 'linkages_r' ||
                      key === 'specialFeatures_1' ||
                      key === 'specialFeatures_2' ||
                      key === 'specialFeatures_3' ||
                      key === 'specialFeatures_4' ||
                      key === 'specialFeatures_5' ||
                      key === 'specialFeatures_r' ||        
                      key === 'total' ? (
                      <Input
                        type="number"
                        id={key}
                        name={key}
                        value={formData[key]}
                        onChange={handleChange}
                        placeholder={fieldLabelMap[key]}
                        aria-label={fieldLabelMap[key]}
                        darkMode={darkMode}
                        hasError={validationErrors[key]}
                      />
                    ) : (
                      <Input
                        type="text"
                        id={key}
                        name={key}
                        value={formData[key]}
                        onChange={handleChange}
                        placeholder={
                          key === 'adjective'
                            ? 'Enter Very Satisfactory'
                            : fieldLabelMap[key]
                        }
                        aria-label={fieldLabelMap[key]}
                        maxLength={
                          key === 'adjective' ? '15'  : '100'
                        }
                        darkMode={darkMode}
                        hasError={validationErrors[key]}
                      />
                    )}
                  </FieldGroup>
                ))}
              </CardSection>
            ))}
            <FormButtonContainer>
              <SubmitButton type="submit" darkMode={darkMode} disabled={isLoading}>
                {isLoading ? 'Updating...' : 'Update Information'}
              </SubmitButton>
            </FormButtonContainer>
          </StyledForm>
        </ErrorBoundary>
      </FormContainer>
    </RightPanel>
  );
};

export default NISFsResultEdit;


// Styled Components
const RightPanel = styled.div`
  position: fixed;
  top: 0;
  right: ${({ show }) => (show ? '0' : '-45%')};
  width: 45%;
  height: 100%;
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#f2f2f2')};
  background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: auto;
  box-shadow: -4px 0 10px rgba(0, 0, 0, 0.2);
  z-index: 5;
  padding: 20px;
  transition: right 0.3s ease-in-out;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FormContainer = styled.div`
  flex: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 5px;
  flex-grow: 1;
  overflow-y: auto;
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.45)' : '#f2f2f2')};
  border-radius: 8px;
`;

const Title = styled.h2`
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 10px 5px;
  margin: 0 0 10px 0;
  font-size: 22px;
  font-weight: bold;
  color: ${({ darkMode }) => (darkMode ? '#ffffff' : '#333')};
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.45)' : '#f2f2f2')};
  border-bottom: 1px solid ${({ darkMode }) => (darkMode ? 'rgba(255, 255, 255, 0.1)' : '#ccc')};
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  span {
    text-align: center;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  font-weight: bold;
  color: ${({ darkMode }) => (darkMode ? '#ffffff' : '#333')};
  cursor: pointer;
  padding: 5px 10px;
  transition: color 0.2s ease;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);

  &:hover {
    color: ${({ darkMode }) => (darkMode ? '#ff5555' : '#cc0000')};
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const LoaderOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.5);
`;

const CardSection = styled.div`
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.3)' : '#fff')};
  backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};
  -webkit-backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};
  padding: 5px;
  margin-bottom: 10px;
  border-radius: 8px;
  box-shadow: ${({ darkMode }) => (darkMode ? '0 3px 8px rgba(255, 255, 255, 0.2)' : '0 3px 10px rgba(0, 0, 0, 0.1)')};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SectionHeader = styled.h3`
  grid-column: span 2;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 5px;
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
  border-bottom: 1px solid ${({ darkMode }) => (darkMode ? '#ccc' : '#ddd')};
  padding-bottom: 5px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 5px;
  color: ${({ darkMode, hasError }) => (hasError ? '#ff0000' : darkMode ? '#f2f2f2' : '#333')};
`;

const RequiredAsterisk = styled.span`
  color: #ff0000;
  margin-left: 4px;
`;

const Input = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${({ hasError }) => (hasError ? '#ff0000' : '#4caf50')};
  border-radius: 4px;
  background-color: ${({ darkMode }) => (darkMode ? '#333' : '#ffffff')};
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
  font-size: 14px;
  width: 100%;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? '#ff0000' : '#388e3c')};
  }

  &:hover {
    background-color: ${({ darkMode }) => (darkMode ? '#444' : '#f1f1f1')};
  }

  &::placeholder {
    color: #888;
    font-style: italic;
    font-size: 14px;
  }
`;

const Select = styled.select`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${({ hasError }) => (hasError ? '#ff0000' : '#4caf50')};
  border-radius: 4px;
  background-color: ${({ darkMode }) => (darkMode ? '#333' : '#ffffff')};
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
  font-size: 14px;
  width: 100%;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? '#ff0000' : '#388e3c')};
  }

  &:hover {
    background-color: ${({ darkMode }) => (darkMode ? '#444' : '#f1f1f1')};
  }

  option {
    color: #333;
    background-color: #fff;
  }
`;

const FormButtonContainer = styled.div`
  text-align: center;
  margin: 10px 0;
`;

const SubmitButton = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: bold;
  background: ${({ darkMode }) => (darkMode ? '#4caf50' : '#18591d')};
  color: #fff;
  border: 1px solid ${({ darkMode }) => (darkMode ? '#388e3c' : '#144d17')};
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ darkMode }) => (darkMode ? '#388e3c' : '#218838')};
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 14px;
  margin-bottom: 10px;
  text-align: center;
`;